import { useState, useEffect, Suspense } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, displayName } from '../lib/auth';
import { api } from '../lib/api';
import { initPushNotifications } from '../lib/push';
import { Home, ClipboardList, Package, Tag, Mail, LogOut, Menu, X, Users, Settings, TrendingUp, ShieldCheck, UserRound } from 'lucide-react';
import FeedbackWidget from './FeedbackWidget';
import MobileTabBar from './MobileTabBar';
import { ConfirmDialog } from './ui';
import { useLiveRefresh } from '../hooks/useLiveRefresh';

export default function DashboardLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Show the admin-console entry point only to platform admins. A normal user's /admin/me
  // call 401/403s (or 404s behind the IP-allowlist) and the link stays hidden.
  const [isAdmin, setIsAdmin] = useState(false);

  // Live unread badge — every 10s while the tab is visible (+ on focus).
  useLiveRefresh(async () => {
    try {
      const { mail, chat } = await api.getUnreadCounts();
      setUnreadCount((mail || 0) + (chat || 0));
    } catch { /* ignore */ }
  }, 10000);

  useEffect(() => {
    let alive = true;
    api.admin.me().then(() => { if (alive) setIsAdmin(true); }).catch(() => { /* not an admin */ });
    return () => { alive = false; };
  }, []);

  // Register for native push (no-op on web until the Capacitor app shell ships).
  useEffect(() => { initPushNotifications(); }, []);

  // Lean, LinkedIn-style nav. Home merges the old Overview + Feed + Network; the Exchange
  // is reached from a prominent button on Home. Settings + Team sit at the bottom.
  const nav = [
    { path: '/dashboard', label: t('dashboardNav.home', 'Home'), icon: Home, exact: true },
    { path: '/dashboard/operations', label: t('dashboardNav.operations', 'Operations'), icon: ClipboardList },
    { path: '/dashboard/inventory', label: t('dashboardNav.inventory', 'Inventory'), icon: Package },
    { path: '/dashboard/offers', label: t('dashboardNav.offers', 'Offers'), icon: Tag },
    { path: '/dashboard/market', label: t('dashboardNav.data', 'Data'), icon: TrendingUp },
    { path: '/dashboard/messages', label: t('dashboardNav.messages', 'Messages'), icon: Mail, badge: unreadCount },
    { path: `/dashboard/profile/${user?.id}`, label: t('dashboardNav.profile', 'Profile'), icon: UserRound },
  ];
  const bottomNav = [
    { path: '/dashboard/settings', label: t('dashboardNav.settings', 'Settings'), icon: Settings },
    ...(user?.role === 'owner' ? [{ path: '/dashboard/team', label: t('dashboardNav.team', 'Team'), icon: Users }] : []),
  ];

  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const SidebarContent = () => (
    <>
      <div className="p-4 border-b border-stone-100">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-7 h-7 rounded-lg bg-stone-950 flex items-center justify-center">
            <span className="text-white text-xs font-bold">M</span>
          </div>
          <span className="text-sm font-bold text-stone-950">MatchMarble</span>
        </Link>
        {user?.company_name && (
          <div className="mt-2 text-[11px] text-stone-500 uppercase tracking-wider truncate" title={user.company_name}>
            {user.company_name}
          </div>
        )}
      </div>
      <nav className="flex-1 p-3 flex flex-col">
        <div className="space-y-0.5">
          {nav.map(({ path, label, icon: Icon, exact, badge }: any) => (
            <Link key={path} to={path} onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                isActive(path, exact)
                  ? 'bg-stone-900 text-white font-medium'
                  : 'text-stone-600 hover:text-stone-950 hover:bg-stone-100'
              }`}>
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {badge > 0 && (
                <span className="bg-accent text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </Link>
          ))}
        </div>
        {/* Settings + Team pinned to the bottom, per the leaner layout. */}
        <div className="mt-auto pt-2 border-t border-stone-100 space-y-0.5">
          {bottomNav.map(({ path, label, icon: Icon }: any) => (
            <Link key={path} to={path} onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                isActive(path)
                  ? 'bg-stone-900 text-white font-medium'
                  : 'text-stone-600 hover:text-stone-950 hover:bg-stone-100'
              }`}>
              <Icon size={16} /><span className="flex-1">{label}</span>
            </Link>
          ))}
          {isAdmin && (
            <Link to="/admin" onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-colors">
              <ShieldCheck size={16} />{t('dashboardNav.adminConsole', 'Admin console')}
            </Link>
          )}
        </div>
      </nav>
      <div className="p-3 border-t border-stone-100">
        {/* Identity block — display only; the profile page lives in the nav above. */}
        <div className="px-3 py-2">
          <div className="text-sm font-medium text-stone-800 truncate">{displayName(user)}</div>
          <div className="text-[11px] text-stone-400 truncate">{user?.email}</div>
        </div>
        <button onClick={() => setConfirmSignOut(true)}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-stone-500 hover:text-stone-800 hover:bg-stone-50 rounded-lg transition-colors">
          <LogOut size={14} />{t('dashboardNav.signOut', 'Sign out')}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-surface">
      {/* Desktop sidebar */}
      <aside className="w-56 bg-white border-r border-stone-100 hidden md:flex flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile header + overlay sidebar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-100 h-14 flex items-center px-4">
        <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-stone-100">
          <Menu size={20} />
        </button>
        <span className="ml-3 text-sm font-bold text-stone-950">MatchMarble</span>
      </div>
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 z-[80] md:hidden" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-white z-[90] flex flex-col md:hidden shadow-2xl">
            <div className="absolute top-3 right-3">
              <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-stone-100"><X size={16} /></button>
            </div>
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-auto md:pt-0 pt-14 pb-tabbar md:pb-0">
        {/* Boundary for code-split dashboard routes — sidebar stays visible while a page chunk loads. */}
        <Suspense fallback={<div className="flex items-center justify-center h-full text-stone-400 text-sm">Loading…</div>}>
          <Outlet />
        </Suspense>
      </div>

      <FeedbackWidget liftForTabBar />
      <MobileTabBar unreadCount={unreadCount} />

      <ConfirmDialog
        open={confirmSignOut}
        title={t('auth.signOutConfirmTitle', 'Sign out?')}
        message={t('auth.signOutConfirmMessage', 'Are you sure you want to sign out of your account?')}
        confirmLabel={t('auth.signOutConfirmYes', 'Sign out')}
        cancelLabel={t('auth.signOutConfirmNo', 'Cancel')}
        onConfirm={() => { setConfirmSignOut(false); logout(); }}
        onCancel={() => setConfirmSignOut(false)}
      />
    </div>
  );
}
